import { BaseSyntheticEvent, FocusEvent, FormEvent, KeyboardEvent, MouseEvent } from 'react';
import { HpClientDebugFlags } from '../../clientDebugFlags';
import { logInfo, recordElapsedTime } from '../../clientLogging';
import { HpFormName } from '../../types/actionTypes';
import { HpEventDetail, HpEventDetailTypes } from '../../types/inputControlTypes';

export interface HpEventHandlerProps<
  EventT extends FormEvent<HTMLElement> | KeyboardEvent<HTMLInputElement>,
  PagePropsT
> {
  event: EventT;
  detail: HpEventDetail;
  pageProps: PagePropsT;
  formName: HpFormName;
}

export type HpHandleFocusEventProps<PagePropsT> = HpEventHandlerProps<FocusEvent<HTMLElement>, PagePropsT>;
export type HpHandleMouseEventProps<PagePropsT> = HpEventHandlerProps<MouseEvent<HTMLElement>, PagePropsT>;
export type HpHandleFormEventProps<PagePropsT> = HpEventHandlerProps<FormEvent<HTMLElement>, PagePropsT>;
export type HpHandleKeyboardEventProps<PagePropsT> = HpEventHandlerProps<KeyboardEvent<HTMLInputElement>, PagePropsT>;

export interface HpEventTypeProcessor<PagePropsT> {
  canHandle(detailType: HpEventDetailTypes): boolean;
  handleFocusEvent(props: HpHandleFocusEventProps<PagePropsT>): void;
  handleMouseEvent(props: HpHandleMouseEventProps<PagePropsT>): void;
  handleFormEvent(props: HpHandleFormEventProps<PagePropsT>): void;
  handleKeyboardEvent(props: HpHandleKeyboardEventProps<PagePropsT>): void;
}

export interface HpEventTypeProcessorItem<PropsT> {
  name: string;
  processor: HpEventTypeProcessor<PropsT>;
}

export class EventProcessor<PropsT> {
  private formName: HpFormName;
  private debugMode: boolean;
  private processors: HpEventTypeProcessorItem<PropsT>[];

  constructor(formName: HpFormName, debugMode: boolean, processors: HpEventTypeProcessorItem<PropsT>[]) {
    this.formName = formName;
    this.debugMode = debugMode;
    this.processors = processors;
  }

  processFocusEvent(event: FocusEvent<HTMLElement>, detail: HpEventDetail, pageProps: PropsT): void {
    this.process(event, (p, d) => p.handleFocusEvent({ event, detail: d, pageProps, formName: this.formName }), detail);
  }

  processMouseEvent(event: MouseEvent<HTMLElement>, detail: HpEventDetail, pageProps: PropsT): void {
    this.process(event, (p, d) => p.handleMouseEvent({ event, detail: d, pageProps, formName: this.formName }), detail);
  }

  processFormEvent(event: FormEvent<HTMLElement>, detail: HpEventDetail, pageProps: PropsT): void {
    this.process(event, (p, d) => p.handleFormEvent({ event, detail: d, pageProps, formName: this.formName }), detail);
  }

  processKeyboardEvent(event: KeyboardEvent<HTMLInputElement>, detail: HpEventDetail, pageProps: PropsT): void {
    this.process(
      event,
      (p, d) => p.handleKeyboardEvent({ event, detail: d, pageProps, formName: this.formName }),
      detail
    );
  }

  private process(
    event: BaseSyntheticEvent,
    action: (p: HpEventTypeProcessor<PropsT>, detail: HpEventDetail) => void,
    detail: HpEventDetail
  ): void {
    recordElapsedTime(`Processing of "${event.type}" event`, () => {
      for (const { name, processor } of this.processors) {
        if (processor.canHandle(detail.type)) {
          recordElapsedTime(`${name} [${event.type}, ${detail.type}]`, () => action(processor, detail));
          break;
        }
      }

      if (this.debugMode) {
        logInfo(`Nobody is handling the '${detail.type}' event :-(`, HpClientDebugFlags.EventProcessor);
      }
    });
  }
}
